import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { resendVerificationEmail } from '@/api/auth/authApi';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { EMAIL_VERIFICATION_REQUIRED_EVENT_NAME } from '@/lib/emailVerificationEvents';

type ResendState = 'idle' | 'sending' | 'sent' | 'failed';

interface EmailVerificationGateProps {
  children: ReactNode;
}

export function EmailVerificationGate({ children }: EmailVerificationGateProps) {
  const { t } = useTranslation('auth');
  const { currentUser, refreshCurrentUser, logOut } = useAuth();
  const [resendState, setResendState] = useState<ResendState>('idle');
  const [isChecking, setIsChecking] = useState(false);
  const [hasCheckedWithoutConfirmation, setHasCheckedWithoutConfirmation] = useState(false);

  useEffect(
    function refreshUserWhenTheServerRequiresVerification() {
      function refreshUser() {
        void refreshCurrentUser();
      }
      window.addEventListener(EMAIL_VERIFICATION_REQUIRED_EVENT_NAME, refreshUser);
      return function stopListening() {
        window.removeEventListener(EMAIL_VERIFICATION_REQUIRED_EVENT_NAME, refreshUser);
      };
    },
    [refreshCurrentUser]
  );

  if (currentUser === null || currentUser.isEmailVerified === true) {
    return <>{children}</>;
  }

  async function sendLinkAgain() {
    setResendState('sending');
    try {
      await resendVerificationEmail();
      setResendState('sent');
    } catch {
      setResendState('failed');
    }
  }

  async function checkConfirmation() {
    setIsChecking(true);
    await refreshCurrentUser();
    setIsChecking(false);
    setHasCheckedWithoutConfirmation(true);
  }

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-zoowhite border border-zooborder rounded-3xl shadow-xl p-7 md:p-9">
        <div className="w-14 h-14 rounded-2xl bg-zoovpalelavender text-zooviolet flex items-center justify-center mb-4">
          <Icon name="Mail" className="text-xl" />
        </div>
        <h1 className="text-xl font-bold text-zooindigo">{t('emailVerificationGate.title')}</h1>
        <p className="text-sm text-zoomuted mt-2">
          {t('emailVerificationGate.body', { email: currentUser.email })}
        </p>
        <p className="text-xs text-zoomuted mt-2">{t('emailVerificationGate.spamHint')}</p>

        {resendState === 'sent' ? (
          <p role="status" className="mt-4 text-sm font-medium text-zoosuccesstext">
            {t('emailVerificationGate.resent')}
          </p>
        ) : null}
        {resendState === 'failed' ? (
          <p role="alert" className="mt-4 text-sm text-zooerrortext">
            {t('emailVerificationGate.resendError')}
          </p>
        ) : null}
        {hasCheckedWithoutConfirmation ? (
          <p role="alert" className="mt-4 text-sm text-zoowarningtext">
            {t('emailVerificationGate.stillUnverified')}
          </p>
        ) : null}

        <div className="mt-6 flex flex-col gap-3">
          <Button
            size="large"
            className="w-full"
            isLoading={isChecking}
            onClick={checkConfirmation}
          >
            {t('emailVerificationGate.confirmed')}
          </Button>
          <Button
            size="large"
            variant="outline"
            className="w-full"
            isLoading={resendState === 'sending'}
            disabled={resendState === 'sent'}
            onClick={sendLinkAgain}
          >
            {t('emailVerificationGate.resend')}
          </Button>
          <button
            type="button"
            className="text-sm font-medium text-zoomuted hover:text-zooindigo"
            onClick={function signOut() {
              void logOut();
            }}
          >
            {t('emailVerificationGate.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}
